import { useEffect } from "react";

// Applies title, meta description, canonical, Open Graph, Twitter, and JSON-LD
// on route mount. Everything except title/description is optional and no-op
// safe. JSON-LD blocks are keyed by a stable data-key so they replace cleanly
// on route change.
const setMeta = (selector, attr, value) => {
  if (!value) return;
  let tag = document.querySelector(selector);
  if (!tag) {
    tag = document.createElement("meta");
    const [key, name] = selector.match(/\[(.*?)="(.*?)"\]/)?.slice(1) || [];
    if (key && name) tag.setAttribute(key, name);
    document.head.appendChild(tag);
  }
  tag.setAttribute(attr, value);
};

const setLink = (rel, href) => {
  if (!href) return;
  let tag = document.querySelector(`link[rel="${rel}"]`);
  if (!tag) {
    tag = document.createElement("link");
    tag.setAttribute("rel", rel);
    document.head.appendChild(tag);
  }
  tag.setAttribute("href", href);
};

const setJsonLd = (blocks) => {
  document.querySelectorAll('script[data-kostaja-jsonld="1"]').forEach((n) => n.remove());
  if (!blocks || !blocks.length) return;
  blocks.forEach((obj, idx) => {
    if (!obj) return;
    const s = document.createElement("script");
    s.type = "application/ld+json";
    s.setAttribute("data-kostaja-jsonld", "1");
    s.setAttribute("data-kostaja-jsonld-idx", String(idx));
    s.text = JSON.stringify(obj);
    document.head.appendChild(s);
  });
};

export const useSeo = (seo = {}) => {
  const { title, description, canonical, ogImage, ogType = "website", twitterCard = "summary_large_image", jsonLd, noindex = false } = seo;

  useEffect(() => {
    if (title) document.title = title;

    if (description) setMeta('meta[name="description"]', "content", description);

    setLink("canonical", canonical);

    // Explicit robots directive on every route. Query-string variants therefore
    // still expose "index,follow" but their canonical points to the clean URL,
    // which is what Google needs to consolidate.
    setMeta('meta[name="robots"]', "content", noindex ? "noindex, nofollow" : "index, follow, max-image-preview:large");

    setMeta('meta[property="og:title"]', "content", title);
    setMeta('meta[property="og:description"]', "content", description);
    setMeta('meta[property="og:type"]', "content", ogType);
    setMeta('meta[property="og:url"]', "content", canonical || (typeof window !== "undefined" ? window.location.href : ""));
    setMeta('meta[property="og:site_name"]', "content", "Kostaja");
    setMeta('meta[property="og:image"]', "content", ogImage || "");

    setMeta('meta[name="twitter:card"]', "content", twitterCard);
    setMeta('meta[name="twitter:title"]', "content", title);
    setMeta('meta[name="twitter:description"]', "content", description);
    if (ogImage) setMeta('meta[name="twitter:image"]', "content", ogImage);

    setJsonLd(jsonLd);

    window.scrollTo({ top: 0, behavior: "instant" in window ? "instant" : "auto" });
    // setMeta/setLink/setJsonLd are stable module-level helpers, intentionally omitted.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [title, description, canonical, ogImage, ogType, twitterCard, jsonLd, noindex]);
};
